import React from 'react';

import {
  EXAM_FOCUS_OPTIONS,
  GUIDE_LENGTH_OPTIONS,
  QUESTIONNAIRE_DELIVERY_OPTIONS,
  isQuestionnaireFocus,
  type ExamFocusId,
  type GuideLengthId,
  type GuideRecipeConfig,
  type QuestionnaireDelivery,
} from '../constants/examAiParams';

export interface ExamAiConfigSidebarProps {
  focus: ExamFocusId;
  onFocusChange: (focus: ExamFocusId) => void;
  isGuide: boolean;
  guideConfig: GuideRecipeConfig;
  onGuideLengthChange: (length: GuideLengthId) => void;
  questionnaireDelivery: QuestionnaireDelivery;
  onQuestionnaireDeliveryChange: (value: QuestionnaireDelivery) => void;
  summaryHint?: string;
}

/**
 * Barra lateral de opciones al estilo Rúbricas (segmentos + chips).
 * Tipo de material docente — no jerga “teórico/práctico/indagación”.
 */
export function ExamAiConfigSidebar({
  focus,
  onFocusChange,
  isGuide,
  guideConfig,
  onGuideLengthChange,
  questionnaireDelivery,
  onQuestionnaireDeliveryChange,
  summaryHint,
}: ExamAiConfigSidebarProps) {
  const focusMeta = EXAM_FOCUS_OPTIONS.find((option) => option.id === focus);
  const showQuestionnaireDelivery = isQuestionnaireFocus(focus);

  return (
    <aside className="rubric-config ai-build-config" aria-label="Opciones del material">
      <div className="rubric-config__scroll">
        <div className="rubric-config__field">
          <p className="rubric-config__label">Tipo de material</p>
          <p className="rubric-config__footer-hint" style={{ marginTop: 0 }}>
            Elige cómo quieres usarlo en el aula.
          </p>
          <div className="rubric-config__segment rubric-config__segment--stack" role="group" aria-label="Tipo de material">
            {EXAM_FOCUS_OPTIONS.map((option) => {
              const active = option.id === focus;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={`rubric-config__seg${active ? ' rubric-config__seg--active' : ''}`}
                  aria-pressed={active}
                  onClick={() => onFocusChange(option.id)}
                >
                  <span className="ai-build-config__seg-label">{option.label}</span>
                  <span className="ai-build-config__seg-detail">{option.description}</span>
                </button>
              );
            })}
          </div>
        </div>

        {showQuestionnaireDelivery ? (
          <div className="rubric-config__field">
            <p className="rubric-config__label">Formato de la práctica</p>
            <p className="rubric-config__footer-hint" style={{ marginTop: 0 }}>
              Es una simulación formativa (no calificación). El formato solo cambia cómo la usás.
            </p>
            <div className="rubric-config__segment" role="group" aria-label="Formato del cuestionario">
              {QUESTIONNAIRE_DELIVERY_OPTIONS.map((option) => {
                const active = option.id === questionnaireDelivery;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`rubric-config__seg${active ? ' rubric-config__seg--active' : ''}`}
                    aria-pressed={active}
                    onClick={() => onQuestionnaireDeliveryChange(option.id)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <p className="rubric-config__footer-hint">
              {
                QUESTIONNAIRE_DELIVERY_OPTIONS.find((o) => o.id === questionnaireDelivery)
                  ?.detail
              }
            </p>
          </div>
        ) : null}

        {isGuide && !showQuestionnaireDelivery ? (
          <div className="rubric-config__field">
            <p className="rubric-config__label">Longitud</p>
            <div className="rubric-config__chips" role="group" aria-label="Longitud de la guía">
              {GUIDE_LENGTH_OPTIONS.map((option) => {
                const active = (guideConfig.guideLength ?? 'medium') === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`rubric-config__chip${active ? ' rubric-config__chip--active' : ''}`}
                    aria-pressed={active}
                    onClick={() => onGuideLengthChange(option.id)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <p className="rubric-config__footer-hint">
              {GUIDE_LENGTH_OPTIONS.find((o) => o.id === (guideConfig.guideLength ?? 'medium'))
                ?.detail}
            </p>
          </div>
        ) : null}

        {summaryHint ? (
          <div className="rubric-config__field">
            <p className="rubric-config__label">Resumen</p>
            <p className="rubric-config__footer-hint" style={{ marginTop: 0 }}>
              {summaryHint}
            </p>
            {focusMeta ? (
              <p className="rubric-config__chip-note">{focusMeta.description}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </aside>
  );
}
